import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { useLocalStorage } from "react-use";

import { ApiService } from "pwapi";

import Spinner from "components/Spinner";
import "./DocumentsPreviewPanel.css";

type DocumentsPreviewPanelProps = {
  documentId: number;
  closePanel: Function;
};

const DocumentsPreviewPanel = ({
  documentId,
  closePanel,
}: DocumentsPreviewPanelProps) => {
  const [QCDocumentsPanelHeight, setQCDocumentsPanelHeight] =
    useLocalStorage<number>("qc_documents_panel_height", 200);

  const [panelHeight, setPanelHeight] = useState(QCDocumentsPanelHeight);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [docInfo, setDocInfo] = useState<{
    document?: any;
    isLoading: boolean;
    error?: boolean;
  }>({ isLoading: false });

  useEffect(() => {
    fetchDocument(documentId);
  }, [documentId]);

  const fetchDocument = async (documentId: number) => {
    setDocInfo({ document: undefined, isLoading: true });
    try {
      const result = await ApiService.retrieveAdminCaseDocument({
        id: documentId,
      });
      setDocInfo({ document: result, isLoading: false });
    } catch (e) {
      setDocInfo({ document: undefined, isLoading: false, error: true });
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      isDragging && setPanelHeight(window.innerHeight - e.clientY + 15);
      e.stopPropagation();
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setIsDragging(false);
        setQCDocumentsPanelHeight(window.innerHeight - e.clientY + 15);
      }
    },
    [isDragging]
  );

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseUp]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleMouseMove]);

  return (
    <div
      className="DocumentsPreview"
      style={{ height: `${panelHeight}px` }}
      data-testid="previewPanel"
    >
      <div className="preview__header">
        <div className="preview__draggingArea" onMouseDown={handleMouseDown}>
          {docInfo.document?.name?.replace(".pdf", "")}
        </div>
        <div className="preview__actions">
          <button
            onClick={() => {
              closePanel(undefined);
            }}
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>
      </div>
      <div
        className="iframe__panel"
        style={{ pointerEvents: isDragging ? "none" : "auto" }}
      >
        {docInfo.isLoading ? (
          <Spinner />
        ) : docInfo.error || !docInfo.document?.presigned_url ? (
          "Missing Document"
        ) : (
          docInfo.document?.presigned_url && (
            <iframe
              src={docInfo.document.presigned_url}
              title="document preview"
              width="100%"
              height="100%"
            />
          )
        )}
      </div>
    </div>
  );
};

export default DocumentsPreviewPanel;
